import React, { useState } from 'react';
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { recordingsAPI, audioBooksAPI } from '@/lib/api';
import { BrutalCard, BrutalBadge, BrutalButton } from '@/components/brutal';
import { Mic, Play, Pause, Trash2, Download, Share2, TrendingUp, BarChart3, Clock, CheckCircle, MessageSquare, Star, Send, X, Link2, Copy, Mail } from 'lucide-react';
import { toast } from 'sonner';
import MediaConsentDialog from './MediaConsentDialog';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const AudioMemoryTab = () => {
  const queryClient = useQueryClient();
  const [playing, setPlaying] = useState(null);
  const [audioEl, setAudioEl] = useState(null);
  const [videoRec, setVideoRec] = useState(null);
  const [videoUrl, setVideoUrl] = useState('');
  const [selectedStudent, setSelectedStudent] = useState('all');
  const [showProgress, setShowProgress] = useState(null);
  const [expandedNotes, setExpandedNotes] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [noteRating, setNoteRating] = useState(0);
  const [shareUrl, setShareUrl] = useState(null);

  // Paged + filtered on the server so the library stays fast with thousands of recordings.
  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ['guardian-recordings', selectedStudent],
    initialPageParam: null,
    queryFn: async ({ pageParam }) => (await recordingsAPI.getGuardianAll({
      limit: 50,
      cursor: pageParam || undefined,
      student_id: selectedStudent === 'all' ? undefined : selectedStudent,
    })).data,
    getNextPageParam: (lastPage) => lastPage.next_cursor || undefined,
  });

  const { data: progressData } = useQuery({
    queryKey: ['diction-progress', showProgress],
    queryFn: async () => (await recordingsAPI.getProgress(showProgress)).data,
    enabled: !!showProgress,
  });

  // Recordings are kept forever; parents can save a copy of the original file.
  const downloadRecording = async (rec) => {
    try {
      const { url } = (await recordingsAPI.download(rec.id)).data;
      window.location.assign(url);
    } catch {
      toast.error('Could not start the download. Please try again.');
    }
  };

  const [consentFor, setConsentFor] = useState(null); // { kind: 'gallery' | 'link', rec }
  const contributeMut = useMutation({
    mutationFn: (data) => audioBooksAPI.contribute(data),
    onSuccess: (res) => {
      queryClient.invalidateQueries(['guardian-recordings']);
      toast.success('Submitted for review. It appears in the collection once our team approves it.');
    },
  });

  const { data: notesData } = useQuery({
    queryKey: ['recording-notes', expandedNotes],
    queryFn: async () => (await recordingsAPI.getNotes(expandedNotes)).data,
    enabled: !!expandedNotes,
  });

  const addNoteMut = useMutation({
    mutationFn: ({ id, data }) => recordingsAPI.addNote(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['recording-notes', expandedNotes]);
      setNoteText('');
      setNoteRating(0);
      toast.success('Note added!');
    },
  });

  const deleteNoteMut = useMutation({
    mutationFn: ({ recordingId, noteId }) => recordingsAPI.deleteNote(recordingId, noteId),
    onSuccess: () => {
      queryClient.invalidateQueries(['recording-notes', expandedNotes]);
      toast.success('Note deleted');
    },
  });

  const shareMut = useMutation({
    mutationFn: (id) => recordingsAPI.createShareLink(id),
    onSuccess: (res) => {
      const url = res.data.share_url;
      setShareUrl(url);
      navigator.clipboard.writeText(url).then(() => toast.success('Share link copied to clipboard!')).catch(() => {});
    },
    onError: () => toast.error('Failed to create share link'),
  });

  const handleAddNote = (recordingId) => {
    if (!noteText.trim()) return toast.error('Please enter a note');
    addNoteMut.mutate({
      id: recordingId,
      data: { note_text: noteText.trim(), star_rating: noteRating || null },
    });
  };

  const playRecording = async (rec) => {
    const id = rec.id;
    const isVideo = rec.recording_type === 'video' || rec.content_type?.startsWith('video/');

    if (playing === id && audioEl) {
      audioEl.pause();
      setPlaying(null);
      return;
    }
    if (audioEl) audioEl.pause();

    if (isVideo) {
      try { setVideoUrl(await recordingsAPI.streamUrl(id)); } catch { toast.error('Video not available'); return; }
      setVideoRec(rec);
      setPlaying(id);
      return;
    }

    let audioUrl;
    try { audioUrl = await recordingsAPI.streamUrl(id); } catch { toast.error('Recording not available'); setPlaying(null); return; }
    const audio = new Audio(audioUrl);
    audio.onerror = () => {
      setPlaying(null);
      toast.error('Recording not available — it may need to be re-recorded');
    };
    audio.onended = () => setPlaying(null);
    audio.play().catch(() => {
      setPlaying(null);
      toast.error('Recording not available — it may need to be re-recorded');
    });
    setAudioEl(audio);
    setPlaying(id);
  };

  if (isLoading) return (
    <div className="text-center py-16" data-testid="audio-memory-loading">
      <div className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full mx-auto mb-3" />
      <p className="font-bold text-gray-500">Loading recordings...</p>
    </div>
  );

  const filtered = (data?.pages || []).flatMap(p => p.recordings || []);
  const students = data?.pages?.[0]?.students || [];

  return (
    <div className="space-y-6" data-testid="audio-memory-tab">
      <BrutalCard shadow="xl" className="bg-gradient-to-r from-violet-50 to-indigo-50">
        <div className="flex items-center gap-3">
          <Mic size={28} className="text-indigo-600" />
          <div>
            <h2 className="text-2xl font-black uppercase">Audio Memories</h2>
            <p className="text-gray-600 font-medium text-sm">Listen to your children reading their stories. A growing collection of precious moments.</p>
          </div>
        </div>
      </BrutalCard>

      {/* Student filter */}
      {students.length > 1 && (
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setSelectedStudent('all')}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold ${selectedStudent === 'all' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-900'}`}>
            All Students
          </button>
          {students.map(s => (
            <button key={s.id} onClick={() => setSelectedStudent(s.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold ${selectedStudent === s.id ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-900'}`}>
              {s.full_name}
            </button>
          ))}
        </div>
      )}

      {/* Recordings list */}
      {filtered.length === 0 ? (
        <BrutalCard shadow="lg" className="text-center py-12">
          <Mic size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-xl font-black mb-2">No Recordings Yet</h3>
          <p className="text-gray-500 font-medium text-sm">When your child reads a story aloud using the Read Aloud feature, their recordings will appear here.</p>
        </BrutalCard>
      ) : (
        <div className="space-y-3">
          {filtered.map(rec => (
            <React.Fragment key={rec.id}>
            <BrutalCard shadow="md" className="hover:shadow-lg transition-shadow" data-testid={`recording-${rec.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <button onClick={() => playRecording(rec)}
                    className="w-10 h-10 flex-shrink-0 rounded-full bg-indigo-600 text-white flex items-center justify-center hover:bg-indigo-700 transition-all"
                    data-testid={`play-${rec.id}`}>
                    {playing === rec.id ? <Pause size={16} /> : <Play size={16} className="ml-0.5" />}
                  </button>
                  <div className="min-w-0">
                    <p className="font-bold text-sm truncate">{rec.narrative_title || 'Story'}</p>
                    <p className="text-xs text-gray-500">
                      {rec.student_name} (age {rec.student_age}) &middot; Ch. {rec.chapter_number} &middot; {new Date(rec.created_date).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' })}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <BrutalBadge variant="indigo" size="sm">Saved</BrutalBadge>
                  {rec.analysis_status === 'completed' && rec.diction_scores ? (
                    <BrutalBadge variant={rec.diction_scores.overall >= 70 ? 'emerald' : rec.diction_scores.overall >= 40 ? 'amber' : 'rose'} size="sm">
                      Analyzed {rec.diction_scores.overall}%
                    </BrutalBadge>
                  ) : rec.analysis_status === 'failed' ? (
                    <BrutalBadge variant="rose" size="sm">Failed</BrutalBadge>
                  ) : rec.analysis_status === 'pending' ? (
                    <BrutalBadge variant="amber" size="sm">Pending</BrutalBadge>
                  ) : null}
                  {!rec.shared_to_audiobooks && rec.analysis_status === 'completed' && (
                    <button onClick={() => setConsentFor({ kind: 'gallery', rec })}
                      className="flex items-center gap-1 px-2 py-1 text-xs font-bold rounded-lg transition-all bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200"
                      title="Allow this recording to be available as an Audio Book"
                      data-testid={`allow-audiobook-${rec.id}`}>
                      <Share2 size={12} /> Allow for Audio Books
                    </button>
                  )}
                  {rec.shared_to_audiobooks && (
                    <span className="flex items-center gap-1 text-xs font-bold text-emerald-500" data-testid={`shared-badge-${rec.id}`}>
                      <CheckCircle size={14} /> Shared
                    </span>
                  )}
                  <button onClick={() => setConsentFor({ kind: 'link', rec })}
                    className="p-2 text-gray-400 hover:text-violet-500 transition-all" title="Share Link"
                    data-testid={`share-${rec.id}`}>
                    <Link2 size={16} />
                  </button>
                  <button onClick={() => { setExpandedNotes(expandedNotes === rec.id ? null : rec.id); setNoteText(''); setNoteRating(0); }}
                    className={`p-2 transition-all ${expandedNotes === rec.id ? 'text-amber-500' : 'text-gray-400 hover:text-amber-500'}`} title="Notes & Rating"
                    data-testid={`notes-${rec.id}`}>
                    <MessageSquare size={16} />
                  </button>
                  <button onClick={() => setShowProgress(showProgress === rec.student_id ? null : rec.student_id)}
                    className="p-2 text-gray-400 hover:text-indigo-600 transition-all" title="View Progress"
                    data-testid={`progress-${rec.student_id}`}>
                    <TrendingUp size={16} />
                  </button>
                  <button onClick={() => downloadRecording(rec)} title="Download this memory"
                    className="p-2 text-gray-400 hover:text-indigo-600 transition-all" data-testid={`download-${rec.id}`}>
                    <Download size={16} />
                  </button>
                </div>
              </div>
            </BrutalCard>

            {/* Notes Panel */}
            {expandedNotes === rec.id && (
              <div className="border-4 border-t-0 border-black p-4 bg-amber-50 -mt-2" data-testid={`notes-panel-${rec.id}`}>
                {/* Average Rating */}
                {notesData?.average_rating > 0 && (
                  <div className="flex items-center gap-2 mb-3">
                    <div className="flex">
                      {[1,2,3,4,5].map(s => (
                        <Star key={s} size={16} className={s <= Math.round(notesData.average_rating) ? 'text-amber-400 fill-amber-400' : 'text-gray-300'} />
                      ))}
                    </div>
                    <span className="text-sm font-bold text-gray-600">{notesData.average_rating} avg ({notesData.total_ratings} ratings)</span>
                  </div>
                )}

                {/* Existing Notes */}
                {notesData?.notes?.length > 0 ? (
                  <div className="space-y-2 mb-4 max-h-48 overflow-y-auto">
                    {notesData.notes.map(note => (
                      <div key={note.id} className="bg-white border-2 border-black p-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs font-black uppercase">{note.user_name}</span>
                              <BrutalBadge variant="indigo" size="sm">{note.user_role}</BrutalBadge>
                              {note.star_rating && (
                                <div className="flex">{[1,2,3,4,5].map(s => (
                                  <Star key={s} size={10} className={s <= note.star_rating ? 'text-amber-400 fill-amber-400' : 'text-gray-300'} />
                                ))}</div>
                              )}
                            </div>
                            <p className="text-sm text-gray-700">{note.note_text}</p>
                            <p className="text-[10px] text-gray-400 mt-1">{new Date(note.created_date).toLocaleString()}</p>
                          </div>
                          <button onClick={() => deleteNoteMut.mutate({ recordingId: rec.id, noteId: note.id })}
                            className="text-gray-300 hover:text-red-500 p-1 flex-shrink-0">
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 mb-3 italic">No notes yet. Be the first to add one!</p>
                )}

                {/* Add Note Form */}
                <div className="border-2 border-black bg-white p-3">
                  <div className="flex items-center gap-1 mb-2">
                    <span className="text-xs font-bold text-gray-500 mr-2">Rating:</span>
                    {[1,2,3,4,5].map(s => (
                      <button key={s} onClick={() => setNoteRating(noteRating === s ? 0 : s)} className="p-0.5">
                        <Star size={18} className={s <= noteRating ? 'text-amber-400 fill-amber-400' : 'text-gray-300 hover:text-amber-300'} />
                      </button>
                    ))}
                    {noteRating > 0 && <span className="text-xs text-gray-400 ml-1">{noteRating}/5</span>}
                  </div>
                  <div className="flex gap-2">
                    <input type="text" value={noteText} onChange={e => setNoteText(e.target.value)}
                      placeholder="Add a note about this recording..."
                      className="flex-1 px-3 py-2 border-2 border-black text-sm font-medium"
                      onKeyDown={e => e.key === 'Enter' && handleAddNote(rec.id)} />
                    <button onClick={() => handleAddNote(rec.id)}
                      disabled={addNoteMut.isPending}
                      className="px-4 py-2 bg-amber-400 border-2 border-black font-bold text-sm hover:bg-amber-500 transition-colors flex items-center gap-1">
                      <Send size={14} /> Add
                    </button>
                  </div>
                </div>
              </div>
            )}
            </React.Fragment>
          ))}
          {hasNextPage && (
            <div className="text-center pt-2">
              <BrutalButton onClick={() => fetchNextPage()} disabled={isFetchingNextPage} data-testid="load-more-recordings">
                {isFetchingNextPage ? 'Loading...' : 'Load more recordings'}
              </BrutalButton>
            </div>
          )}
        </div>
      )}

      {/* Share Link Modal */}
      {shareUrl && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShareUrl(null)}>
          <div className="bg-white border-4 border-black p-6 max-w-md w-full shadow-[8px_8px_0_0_rgba(0,0,0,1)]" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-black uppercase flex items-center gap-2"><Link2 size={20} /> Share Link</h3>
              <button onClick={() => setShareUrl(null)} className="text-gray-400 hover:text-black"><X size={20} /></button>
            </div>
            <p className="text-sm text-gray-500 mb-3">Anyone with this link can listen to this recording (expires in 30 days):</p>
            <div className="flex gap-2 mb-4">
              <input readOnly value={shareUrl} className="flex-1 px-3 py-2 border-2 border-black text-sm font-mono bg-gray-50" />
              <button onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success('Copied!'); }}
                className="px-3 py-2 bg-indigo-500 text-white border-2 border-black font-bold text-sm hover:bg-indigo-600 flex items-center gap-1">
                <Copy size={14} /> Copy
              </button>
            </div>
            <a href={`mailto:?subject=Listen to this reading!&body=Check out this recording: ${encodeURIComponent(shareUrl)}`}
              className="flex items-center justify-center gap-2 w-full py-2 bg-amber-50 border-2 border-black font-bold text-sm hover:bg-amber-100 transition-colors">
              <Mail size={14} /> Share via Email
            </a>
          </div>
        </div>
      )}

      {/* Video Player Modal */}
      {consentFor && (
        <MediaConsentDialog
          kind={consentFor.kind}
          onCancel={() => setConsentFor(null)}
          onConfirm={() => {
            const { kind, rec } = consentFor;
            setConsentFor(null);
            if (kind === 'gallery') contributeMut.mutate({ recording_id: rec.id, display_name: rec.student_name, consent: true });
            else shareMut.mutate(rec.id);
          }}
        />
      )}
      {videoRec && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => { setVideoRec(null); setPlaying(null); }}>
          <div className="bg-white rounded-xl max-w-2xl w-full overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-3 border-b">
              <div>
                <p className="font-bold text-sm">{videoRec.narrative_title || 'Video Recording'}</p>
                <p className="text-xs text-gray-500">{videoRec.student_name} &middot; Ch. {videoRec.chapter_number}</p>
              </div>
              <button onClick={() => { setVideoRec(null); setPlaying(null); }} className="text-gray-400 hover:text-gray-800 font-bold text-lg">&times;</button>
            </div>
            <video
              src={videoUrl}
              controls
              autoPlay
              className="w-full"
              onEnded={() => { setVideoRec(null); setPlaying(null); }}
              onError={() => { setVideoRec(null); setPlaying(null); toast.error('Video not available'); }}
            />
          </div>
        </div>
      )}

      {/* Diction Progress Panel */}
      {showProgress && progressData && (
        <BrutalCard shadow="xl" className="bg-gradient-to-br from-emerald-50 to-teal-50" data-testid="diction-progress-panel">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 size={22} className="text-emerald-600" />
            <h3 className="text-lg font-black uppercase">Diction Progress</h3>
            <button onClick={() => setShowProgress(null)} className="ml-auto text-xs font-bold text-gray-500 hover:text-gray-800">Close</button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <div className="text-center p-3 bg-white rounded-lg">
              <p className="text-2xl font-black text-indigo-600">{progressData.total_sessions}</p>
              <p className="text-xs font-bold text-gray-500 uppercase">Sessions</p>
            </div>
            {Object.entries(progressData.improvement || {}).map(([key, val]) => (
              <div key={key} className="text-center p-3 bg-white rounded-lg">
                <p className={`text-2xl font-black ${val >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                  {val >= 0 ? '+' : ''}{val}%
                </p>
                <p className="text-xs font-bold text-gray-500 uppercase">{key}</p>
              </div>
            ))}
          </div>
          {progressData.progress?.length > 0 && (
            <div className="space-y-1">
              {progressData.progress.slice(-10).map((p, i) => (
                <div key={i} className="flex items-center justify-between text-xs p-2 bg-white rounded-lg">
                  <span className="font-medium truncate flex-1">{p.title}</span>
                  <span className="text-gray-500 mx-2">{new Date(p.date).toLocaleDateString()}</span>
                  <span className="font-black text-indigo-600">{p.scores?.overall}%</span>
                </div>
              ))}
            </div>
          )}
        </BrutalCard>
      )}
    </div>
  );
};

export default AudioMemoryTab;
